import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { FaBolt, FaCoins, FaGlobe, FaShieldAlt, FaSyncAlt, FaEthereum } from 'react-icons/fa';
import Button from '../components/Button';
import SectionTitle from '../components/SectionTitle';
import FeatureCard from '../components/FeatureCard';
import heroBg from '../assets/logo.png';
import whiteLogo from '../assets/logo-white.png';

const HeroSection = styled.section`
  background: linear-gradient(125deg, #060d21 0%, #0f2942 100%);
  padding: 8rem 0;
  position: relative;
  overflow: hidden;
  color: white;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 50%;
    height: 100%;
    background-image: url(${heroBg});
    background-repeat: no-repeat;
    background-position: center right;
    background-size: contain;
    opacity: 0.15;
    filter: drop-shadow(0 0 30px rgba(0, 213, 146, 0.4));
    animation: float 8s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 20%, rgba(0, 213, 146, 0.15) 0%, transparent 30%),
      radial-gradient(circle at 80% 80%, rgba(0, 213, 146, 0.1) 0%, transparent 40%),
      repeating-linear-gradient(
        45deg,
        rgba(0, 213, 146, 0.02) 0%,
        rgba(0, 213, 146, 0.02) 1px,
        transparent 1px,
        transparent 10px
      );
    z-index: 1;
    pointer-events: none;
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0) rotate(0deg);
    }
    50% {
      transform: translateY(-15px) rotate(1deg);
    }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 5rem 0;
    
    &::before {
      width: 100%;
      background-position: center;
    }
  }
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 2.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`;

const HeroContent = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -30px;
    left: -50px;
    width: 100px;
    height: 100px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.4) 0%, transparent 70%);
    border-radius: 50%;
    opacity: 0.5;
    filter: blur(20px);
    animation: pulse 8s ease-in-out infinite alternate;
  }
  
  @keyframes pulse {
    0% { opacity: 0.2; transform: scale(0.8); }
    100% { opacity: 0.6; transform: scale(1.2); }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    order: 2;
    text-align: center;
    
    ${ButtonGroup} {
      justify-content: center;
    }
    
    &::before {
      left: 0;
      top: -20px;
    }
  }
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: white;
  position: relative;
  text-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  font-weight: 800;
  letter-spacing: -0.5px;
  
  span {
    color: ${props => props.theme.colors.primary};
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 5px;
      background: ${props => props.theme.colors.primary};
      bottom: 5px;
      left: 0;
      opacity: 0.3;
      filter: blur(4px);
    }
  }
  
  &::before {
    content: '//';
    font-size: 1.5rem;
    position: absolute;
    top: -10px;
    left: 0;
    color: ${props => props.theme.colors.primary};
    opacity: 0.7;
    font-weight: 500;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 3rem;
    
    &::before {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const HeroDescription = styled.p`
  font-size: 1.3rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  color: rgba(255, 255, 255, 0.85);
  position: relative;
  max-width: 580px;
  
  &::after {
    content: '';
    position: absolute;
    height: 50px;
    width: 3px;
    background: linear-gradient(to bottom, ${props => props.theme.colors.primary}, transparent);
    top: 10px;
    left: -20px;
    border-radius: 3px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 1.1rem;
    margin: 0 auto 2rem;
    
    &::after {
      display: none;
    }
  }
`;

const HeroImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 2;
  
  &::before {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.3) 0%, transparent 70%);
    z-index: -1;
    animation: spin 25s  infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 250px;
    height: 250px;
    border-radius: 50%;
    border: 1px dashed rgba(0, 213, 146, 0.3);
    z-index: -1;
    animation: spin 25s infinite;
  }

  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    order: 1;
  }
`;

const HeroImage = styled.img`
  max-width: 85%;
  filter: drop-shadow(0 0 40px rgba(0, 213, 146, 0.5));
  transform-style: preserve-3d;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    max-width: 65%;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    max-width: 80%;
  }
`;

const Section = styled.section`
  padding: 8rem 0;
  background-color: ${props => props.alternate ? 'rgba(240, 247, 255, 0.5)' : props.theme.colors.white};
  position: relative;
  overflow: hidden;
  
  ${props => !props.alternate && `
    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: 
        radial-gradient(circle at 15% 15%, rgba(0, 213, 146, 0.04) 0%, transparent 35%),
        radial-gradient(circle at 85% 85%, rgba(0, 213, 146, 0.04) 0%, transparent 35%);
      z-index: 0;
    }
  `}
  
  ${props => props.alternate && `
    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: 
        radial-gradient(circle at 10% 90%, rgba(0, 213, 146, 0.07) 0%, transparent 30%),
        radial-gradient(circle at 90% 10%, rgba(0, 213, 146, 0.07) 0%, transparent 40%);
      z-index: 0;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 1px;
      background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(0, 213, 146, 0.3) 50%, 
        transparent 100%
      );
    }
  `}
  
  /* Subtle pattern overlay */
  position: relative;
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300d592' fill-opacity='0.025'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
    z-index: 0;
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2.5rem;
  margin-top: 4rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    left: -150px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.05) 0%, transparent 70%);
    border-radius: 50%;
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    right: -150px;
    width: 250px;
    height: 250px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.03) 0%, transparent 70%);
    border-radius: 50%;
    z-index: 0;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StatsSection = styled.section`
  background: linear-gradient(135deg, #060d21 0%, #0a192f 100%);
  padding: 7rem 0;
  color: ${props => props.theme.colors.white};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: 
      radial-gradient(circle at top right, rgba(0, 213, 146, 0.2), transparent 50%),
      radial-gradient(circle at bottom left, rgba(0, 213, 146, 0.15), transparent 50%),
      url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300d592' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: repeating-linear-gradient(
      45deg,
      rgba(0, 213, 146, 0.05),
      rgba(0, 213, 146, 0.05) 1px,
      transparent 1px,
      transparent 10px
    );
    pointer-events: none;
  }
`;

const StatsContent = styled.div`
  position: relative;
  z-index: 2;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  text-align: center;
  padding: 2.5rem;
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: linear-gradient(135deg, rgba(0, 213, 146, 0.1) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 80px;
    height: 80px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.4) 0%, transparent 70%);
    border-radius: 50%;
    bottom: -40px;
    right: -40px;
    opacity: 0.5;
    filter: blur(20px);
  }
  
  &:hover {
    transform: translateY(-10px) scale(1.05);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 15px 20px rgba(0, 0, 0, 0.2);
    border-color: rgba(0, 213, 146, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
`;

const StatNumber = styled.h3`
  font-size: 3.5rem;
  margin-bottom: 0.8rem;
  background: linear-gradient(120deg, ${props => props.theme.colors.primary}, #a0ffe3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  position: relative;
  text-shadow: 0 10px 30px rgba(0, 213, 146, 0.3);
  
  &::after {
    content: '';
    position: absolute;
    width: 40px;
    height: 3px;
    background: ${props => props.theme.colors.primary};
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 3px;
    box-shadow: 0 0 10px rgba(0, 213, 146, 0.5);
  }
`;

const StatTitle = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-top: 1rem;
  position: relative;
  display: inline-block;
  padding: 0.5rem 1rem;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    border: 1px solid rgba(0, 213, 146, 0.3);
    border-radius: 4px;
    opacity: 0.6;
  }
`;

const HowItWorksContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  margin-top: 4rem;
  position: relative;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ProcessLine = styled.div`
  position: absolute;
  top: 65px;
  left: calc(50px + 2rem);
  right: calc(50px + 2rem);
  height: 3px;
  background: linear-gradient(90deg, 
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.primary} 85%,
    rgba(0, 213, 146, 0.3) 100%
  );
  z-index: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const StepCard = styled.div`
  background-color: ${props => props.theme.colors.white};
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.05);
  text-align: center;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 213, 146, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.theme.colors.primary};
  }
`;

const StepNumber = styled.div`
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, #00a070);
  color: ${props => props.theme.colors.white};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 auto 2rem;
  position: relative;
  box-shadow: 0 10px 20px rgba(0, 213, 146, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 50%;
    border: 2px dashed rgba(0, 213, 146, 0.3);
    animation: spin 20s linear infinite;
  }
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const StepTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1.2rem;
  color: ${props => props.theme.colors.secondary};
  position: relative;
  padding-bottom: 0.8rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background: ${props => props.theme.colors.primary};
  }
`;

const StepDescription = styled.p`
  color: ${props => props.theme.colors.gray};
  font-size: 1rem;
  line-height: 1.6;
`;

const CTASection = styled.section`
  padding: 8rem 0;
  background: linear-gradient(135deg, #0a0f1a 0%, #0d1a2f 50%, #102342 100%);
  color: ${props => props.theme.colors.white};
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.08) 0%, rgba(0, 213, 146, 0) 70%);
    animation: pulse 15s ease-in-out infinite;
    opacity: 0.8;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      repeating-linear-gradient(
        45deg,
        rgba(0, 213, 146, 0.03),
        rgba(0, 213, 146, 0.03) 1px,
        transparent 1px,
        transparent 10px
      ),
      url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%2300d592' fill-opacity='0.03' fill-rule='evenodd'/%3E%3C/svg%3E");
  }
  
  @keyframes pulse {
    0% {
      transform: translate(0, 0) scale(1);
      opacity: 0.5;
    }
    50% {
      transform: translate(-5%, -5%) scale(1.05);
      opacity: 0.7;
    }
    100% {
      transform: translate(0, 0) scale(1);
      opacity: 0.5;
    }
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 10;
  padding: 0 1rem;
  
  &::before {
    content: '';
    position: absolute;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.3) 0%, transparent 70%);
    border-radius: 50%;
    top: -50px;
    left: 50%;
    transform: translateX(-50%);
    filter: blur(30px);
    z-index: -1;
  }
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  margin-bottom: 2rem;
  text-shadow: 0 0 20px rgba(0, 213, 146, 0.5);
  position: relative;
  display: inline-block;
  font-weight: 800;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  &::before {
    content: '/>';
    position: absolute;
    top: -25px;
    left: 0;
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    opacity: 0.8;
    font-weight: 400;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 4px;
    background: linear-gradient(90deg, 
      rgba(0, 213, 146, 0) 0%, 
      rgba(0, 213, 146, 1) 50%,
      rgba(0, 213, 146, 0) 100%
    );
    border-radius: 4px;
    box-shadow: 0 0 20px rgba(0, 213, 146, 0.5);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: 2.2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.3rem;
  margin-bottom: 3rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.9);
  position: relative;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 40px;
    height: 2px;
    background: rgba(0, 213, 146, 0.5);
  }
  
  &::before {
    left: 20%;
    top: -15px;
  }
  
  &::after {
    right: 20%;
    bottom: -15px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: 1.1rem;
    
    &::before, &::after {
      width: 30px;
    }
  }
`;

const Home = () => {
  return (
    <>
      <Helmet>
        <title>RDT Chain - Zero-Fee Blockchain for Global Payments</title>
      </Helmet>
      <HeroSection>
        <HeroContainer>
          <HeroContent>
            <HeroTitle>
              Fast & Free <span>Blockchain</span> Ecosystem
            </HeroTitle>
            <HeroDescription>
              RDT Chain is a high-performance blockchain designed for zero-fee cross-border payments,
              enabling instant and secure global transactions.
            </HeroDescription>
            <ButtonGroup>
              <Button href="https://explorer.rdtchain.com" target="_blank" rel="noopener noreferrer">
                Explore Chain
              </Button>
              <Button to="/developers" secondary>
                Start Building
              </Button>
            </ButtonGroup>
          </HeroContent>
          <HeroImageContainer>
            <HeroImage src={whiteLogo} alt="RDT Chain" />
          </HeroImageContainer>
        </HeroContainer>
      </HeroSection>

      <Section>
        <Container>
          <SectionTitle
            subtitle="Key Features"
            title="Why Choose RDT Chain?"
            description="RDT Chain combines the best aspects of blockchain technology to create a fast, secure, and accessible payment system."
            center
          />
          <FeaturesGrid>
            <FeatureCard
              icon={<FaBolt />}
              title="Ultra-Fast Transactions"
              description="Process transactions in just 5 seconds, making RDT Chain perfect for real-time payment solutions."
            />
            <FeatureCard
              icon={<FaCoins />}
              title="Zero Transaction Fees"
              description="Send and receive payments without any transaction fees, saving costs for both businesses and individuals."
            />
            <FeatureCard
              icon={<FaGlobe />}
              title="Cross-Border Payments"
              description="Seamlessly make payments across borders without the delays and high fees of traditional banking systems."
            />
            <FeatureCard
              icon={<FaShieldAlt />}
              title="Proof of Authority"
              description="Secure consensus mechanism ensuring network reliability while being energy-efficient."
            />
            <FeatureCard
              icon={<FaSyncAlt />}
              title="5-Second Block Time"
              description="Regular block production every 5 seconds ensures consistent and predictable transaction confirmation."
            />
            <FeatureCard
              icon={<FaEthereum />}
              title="EVM Compatible"
              description="Compatible with Ethereum tools like MetaMask, making it easy to integrate with the existing ecosystem."
            />
          </FeaturesGrid>
        </Container>
      </Section>

      <StatsSection>
        <Container>
          <StatsContent>
            <StatsGrid>
              <StatCard>
                <StatNumber>0</StatNumber>
                <StatTitle>FEES</StatTitle>
              </StatCard>
              <StatCard>
                <StatNumber>5s</StatNumber>
                <StatTitle>BLOCK TIME</StatTitle>
              </StatCard>
              <StatCard>
                <StatNumber>2.5T</StatNumber>
                <StatTitle>RDT SUPPLY</StatTitle>
              </StatCard>
              <StatCard>
                <StatNumber>100%</StatNumber>
                <StatTitle>DECENTRALIZED</StatTitle>
              </StatCard>
            </StatsGrid>
          </StatsContent>
        </Container>
      </StatsSection>

      <Section alternate>
        <Container>
          <SectionTitle
            subtitle="Simple Process"
            title="How to Get Started"
            description="Join the RDT Chain ecosystem in just a few simple steps."
            center
          />
          <HowItWorksContainer>
            <ProcessLine />
            <StepCard>
              <StepNumber>1</StepNumber>
              <StepTitle>Connect Your Wallet</StepTitle>
              <StepDescription>
                Use MetaMask or any EVM compatible wallet to connect to the RDT Chain network, with seamless integration.
              </StepDescription>
            </StepCard>
            <StepCard>
              <StepNumber>2</StepNumber>
              <StepTitle>Add RDT Chain Network</StepTitle>
              <StepDescription>
                Add RDT Chain to your wallet by configuring the RPC endpoint and Chain ID for instant access.
              </StepDescription>
            </StepCard>
            <StepCard>
              <StepNumber>3</StepNumber>
              <StepTitle>Start Transacting</StepTitle>
              <StepDescription>
                Begin sending and receiving global payments with zero transaction fees and 5-second confirmations.
              </StepDescription>
            </StepCard>
          </HowItWorksContainer>
        </Container>
      </Section>

      <CTASection>
        <Container>
          <CTAContent>
            <CTATitle>Experience the Future of Cross-Border Payments</CTATitle>
            <CTADescription>
              RDT Chain eliminates transaction fees and enables instant cross-border transfers, opening a new era of global financial freedom.
            </CTADescription>
            <ButtonGroup center style={{
              alignSelf: 'center',
              display: 'flex',
              justifyContent: 'center',
              gap: '1.5rem',
              marginTop: '2.5rem'
            }}>
              <Button to="/wallet">
                Get RDT Wallet
              </Button>
              <Button to="/developers" secondary>
                Start Building
              </Button>
            </ButtonGroup>
          </CTAContent>
        </Container>
      </CTASection>
    </>
  );
};

export default Home;